import React, { useMemo, useState, useCallback, useEffect } from 'react';
import {
    Box,
    Flex,
    HStack,
    VStack,
    Text,
    Heading,
    Icon,
    SimpleGrid,
    Badge,
    Input,
    Grid,
    GridItem,
    Button,
    IconButton,
    Link,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    Avatar,
    Divider,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    useDisclosure,
    useColorMode,
    useToast,
} from '@chakra-ui/react';
import {
    AiOutlineTrophy,
    AiOutlineSchedule,
    AiOutlineDollarCircle,
    AiOutlinePartition,
    AiOutlineBarChart,
    AiOutlineCloseCircle,
    AiOutlinePlus,
    AiOutlineDelete,
    AiOutlineFilePdf,
    AiOutlinePieChart,
    AiOutlineTable,
    AiOutlineRise,
    AiOutlineShop,
    AiOutlineUser,
    AiOutlineCalendar,
    AiOutlineVideoCamera,
    AiOutlineRollback,
} from 'react-icons/ai';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as ChartTooltip,
    PieChart,
    Pie,
    Cell,
    LineChart,
    Line,
} from 'recharts';
import { useComercial } from '../../../../contexts/ComercialContext';
import { usePermissions } from '../../../../contexts/AuthPermissionsContext';
import { useNogleTech } from '../../../../contexts/NogleTechContext';
import api from '../../../../services/api';
import SearchableSelect from '../../../../components/UI/SearchableSelect';
import ConfirmActionDialog from '../../../../components/UI/ConfirmActionDialog';

const ITENS_POR_PAGINA = 15;

const fmtMoney = (v) =>
    `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtInt = (v) => Number(v || 0).toLocaleString('pt-BR');
const fmtBR = (iso) => {
    if (!iso) return '—';
    const m = String(iso).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : String(iso);
};

const ABAS = [
    { key: 'relatorios', label: 'Relatórios', icon: AiOutlineBarChart },
    { key: 'negocios',   label: 'Negócios',   icon: AiOutlinePartition },
    { key: 'atividades', label: 'Atividades', icon: AiOutlineSchedule },
];

const InfoChip = ({ icon, children }) => (
    <HStack spacing={1.5} color="textSecondary" fontSize="xs">
        <Icon as={icon} boxSize="13px" color="textMuted" />
        <Text noOfLines={1}>{children}</Text>
    </HStack>
);

const TIPO_META = {
    ganho:   { label: 'Ganho',      cor: '#01E975' },
    perdido: { label: 'Perdido',    cor: '#FC8181' },
    aberto:  { label: 'Em aberto',  cor: '#718096' },
};

const PALETTE = ['#01E975', '#4299E1', '#9F7AEA', '#ED8936', '#ECC94B', '#FC8181', '#48BB78', '#F687B3', '#38B2AC', '#718096'];

const DIMENSOES = [
    { v: 'funil',       l: 'Funil' },
    { v: 'etapa',       l: 'Etapa do funil' },
    { v: 'resultado',   l: 'Resultado (ganho/perdido)' },
    { v: 'canal',       l: 'Canal de origem' },
    { v: 'responsavel', l: 'Responsável' },
    { v: 'mes',         l: 'Mês de fechamento' },
];
const METRICAS = [
    { v: 'count',      l: 'Nº de negócios', money: false },
    { v: 'valorTotal', l: 'Valor total',    money: true },
    { v: 'valorGanho', l: 'Valor ganho',    money: true },
];
const VIZ = [
    { v: 'bar',   l: 'Barras', icon: AiOutlineBarChart },
    { v: 'pie',   l: 'Pizza',  icon: AiOutlinePieChart },
    { v: 'table', l: 'Tabela', icon: AiOutlineTable },
];

const WIDGETS_KEY = 'nogle_relatorios_widgets';

const KpiCard = ({ icon, color, label, value, sub }) => (
    <Box bg="surface" border="1px solid" borderColor="borderPrimary" borderRadius="xl" p={4}>
        <HStack justify="space-between" mb={2}>
            <Text fontSize="10px" color="textMuted" textTransform="uppercase" letterSpacing="1px" fontWeight="800">{label}</Text>
            <Icon as={icon} color={color} boxSize="18px" />
        </HStack>
        <Text fontSize="2xl" fontWeight="900" color="textPrimary">{value}</Text>
        {sub && <Text fontSize="10px" color="textSecondary" mt={1}>{sub}</Text>}
    </Box>
);

const Relatorios = () => {
    const { pipelineCards, atividades, pipelines, reabrirItem, resultadosNegocio } = useComercial();
    const { colorMode } = useColorMode();
    const { employees } = usePermissions();
    const { marcas } = useNogleTech();
    const toast = useToast();

    const [aba, setAba] = useState('relatorios');
    const [reabrindo, setReabrindo] = useState(false);
    // Confirmação antes de retornar item ao Pipeline/Atividades — evita clique acidental
    const [confirmReabrirAberto, setConfirmReabrirAberto] = useState(false);
    const [filtroFunil, setFiltroFunil] = useState('todos');
    const { isOpen: isDetOpen, onOpen: onDetOpen, onClose: onDetClose } = useDisclosure();
    const [detalhe, setDetalhe] = useState(null); // { kind:'negocio'|'atividade', data, etapa }

    const abrirDetalhe = useCallback((kind, data, etapa) => {
        setDetalhe({ kind, data, etapa });
        onDetOpen();
    }, [onDetOpen]);

    const handleReabrir = useCallback(async () => {
        if (!detalhe) return;
        const tipo = detalhe.kind === 'negocio' ? 'negocio' : 'atividade';
        setReabrindo(true);
        try {
            await reabrirItem(tipo, detalhe.data.id);
            toast({
                title: tipo === 'negocio' ? 'Negócio devolvido ao Pipeline' : 'Atividade devolvida às Atividades',
                status: 'success', duration: 2500,
            });
            onDetClose();
        } catch (err) {
            console.error('Erro ao reabrir item:', err);
            toast({ title: 'Erro ao reabrir', description: 'Tente novamente.', status: 'error', duration: 3000 });
        } finally {
            setReabrindo(false);
        }
    }, [detalhe, reabrirItem, toast, onDetClose]);
    const [filtroResponsavel, setFiltroResponsavel] = useState('todos');
    const [filtroCanal, setFiltroCanal] = useState('todos');
    const [filtroResultado, setFiltroResultado] = useState('todos');
    const [filtroDataInicio, setFiltroDataInicio] = useState('');
    const [filtroDataFim, setFiltroDataFim] = useState('');
    const [canais, setCanais] = useState([]);

    // Construtor de análises personalizadas (persistido em localStorage)
    const [widgets, setWidgets] = useState(() => {
        try { return JSON.parse(localStorage.getItem(WIDGETS_KEY)) || []; } catch { return []; }
    });
    const [novo, setNovo] = useState({ dimensao: 'etapa', metrica: 'count', viz: 'bar' });

    useEffect(() => {
        localStorage.setItem(WIDGETS_KEY, JSON.stringify(widgets));
    }, [widgets]);

    useEffect(() => {
        api.get('/comercial/dropdowns/canais').then(r => setCanais(r.data || [])).catch(() => {});
    }, []);

    useEffect(() => { setPageNeg(1); setPageAtv(1); }, [filtroFunil, filtroResponsavel, filtroCanal, filtroResultado, filtroDataInicio, filtroDataFim, aba]);

    // Estilo de impressão do relatório
    useEffect(() => {
        const id = 'nogle-report-print-style';
        if (document.getElementById(id)) return;
        const style = document.createElement('style');
        style.id = id;
        style.innerHTML = `
            @media print {
                body * { visibility: hidden !important; }
                #relatorio-print, #relatorio-print * { visibility: visible !important; }
                #relatorio-print { position: absolute !important; left: 0; top: 0; width: 100%; padding: 24px; }
                .no-print { display: none !important; }
                .only-print { display: block !important; }
            }
            .only-print { display: none; }
        `;
        document.head.appendChild(style);
    }, []);

    const etapaDeCard = useCallback((card) => {
        for (const pipe of pipelines || []) {
            const col = (pipe.colunas || []).find(c => String(c.id) === String(card.colunaId));
            if (col) return { id: col.id, label: col.label || col.nome || 'Etapa', cor: col.cor || '#718096', tipo: col.tipo || 'aberto' };
        }
        return { id: 'sem-etapa', label: 'Sem etapa', cor: '#718096', tipo: 'aberto' };
    }, [pipelines]);

    const resultadoDeCard = useCallback((card) => {
        if (card.canceled) return 'cancelado';
        // Classificação pelo PAPEL do resultado (1 positivo/ganho · 2 negativo/perdido) —
        // o nome do resultado é cosmético. Fallbacks: nome legado e tipo da coluna.
        if (card.result_role === 1) return 'ganho';
        if (card.result_role === 2) return 'perdido';
        if (card.resultado && (card.result_role === 0 || card.result_role == null)) {
            const res = String(card.resultado).toLowerCase();
            if (res.includes('ganho')) return 'ganho';
            if (res.includes('perdido') || res.includes('perda')) return 'perdido';
        }
        const et = etapaDeCard(card);
        if (et.tipo === 'ganho' || et.tipo === 'perdido') return et.tipo;
        return 'aberto';
    }, [etapaDeCard]);

    // Resolve empresa/responsável pelo cliente (o JOIN do backend pode vir vazio)
    const resolverEmpresa = useCallback((card) => {
        const m = (marcas || []).find(x => String(x.id) === String(card.company_id));
        return m?.nome || card.empresa || '';
    }, [marcas]);
    const resolverResponsavel = useCallback((card) => {
        const e = (employees || []).find(x => String(x.id) === String(card.assignee_id));
        return e?.name || card.responsavel || '';
    }, [employees]);

    const [pageNeg, setPageNeg] = useState(1);
    const [pageAtv, setPageAtv] = useState(1);

    const cardsConcluidos = useMemo(() => (pipelineCards || []).filter(c => c.completed), [pipelineCards]);
    const atividadesConcluidas = useMemo(() => (atividades || []).filter(a => a.completed), [atividades]);

    const selectedEmployee = useMemo(() => {
        if (filtroResponsavel === 'todos') return null;
        return (employees || []).find(e => String(e.id) === String(filtroResponsavel));
    }, [filtroResponsavel, employees]);

    const filteredCards = useMemo(() => cardsConcluidos.filter(card => {
        if (filtroFunil !== 'todos' && String(card.pipelineId) !== String(filtroFunil)) return false;
        if (filtroResponsavel !== 'todos') {
            const matchId = String(card.assignee_id) === String(filtroResponsavel);
            const matchName = selectedEmployee && card.responsavel &&
                String(card.responsavel).toLowerCase() === String(selectedEmployee.name).toLowerCase();
            if (!matchId && !matchName) return false;
        }
        if (filtroCanal !== 'todos' && (!card.canal || String(card.canal).toLowerCase() !== String(filtroCanal).toLowerCase())) return false;
        if (filtroResultado !== 'todos' && resultadoDeCard(card) !== filtroResultado) return false;
        if (filtroDataInicio && (!card.dataFechamento || card.dataFechamento < filtroDataInicio)) return false;
        if (filtroDataFim && (!card.dataFechamento || card.dataFechamento > filtroDataFim)) return false;
        return true;
    }), [cardsConcluidos, filtroFunil, filtroResponsavel, selectedEmployee, filtroCanal, filtroResultado, filtroDataInicio, filtroDataFim, resultadoDeCard]);

    const filteredAtividades = useMemo(() => atividadesConcluidas.filter(atv => {
        if (filtroResponsavel !== 'todos') {
            const matchId = String(atv.assignee_id) === String(filtroResponsavel);
            const matchName = selectedEmployee && atv.responsavel &&
                String(atv.responsavel).toLowerCase() === String(selectedEmployee.name).toLowerCase();
            if (!matchId && !matchName) return false;
        }
        if (filtroDataInicio && (!atv.dataInicio || atv.dataInicio < filtroDataInicio)) return false;
        if (filtroDataFim && (!atv.dataInicio || atv.dataInicio > filtroDataFim)) return false;
        return true;
    }), [atividadesConcluidas, filtroResponsavel, selectedEmployee, filtroDataInicio, filtroDataFim]);

    // Negócios agrupados pela ETAPA REAL (aba Negócios)
    const grupos = useMemo(() => {
        const map = {};
        for (const card of filteredCards) {
            const e = etapaDeCard(card);
            if (!map[e.id]) map[e.id] = { ...e, cards: [], total: 0, valor: 0 };
            map[e.id].cards.push(card);
            map[e.id].total += 1;
            map[e.id].valor += Number(card.valor) || 0;
        }
        const ordemTipo = { ganho: 0, aberto: 1, perdido: 2 };
        return Object.values(map).sort((a, b) => (ordemTipo[a.tipo] - ordemTipo[b.tipo]) || (b.valor - a.valor));
    }, [filteredCards, etapaDeCard]);

    // Motor de agregação (dimensão × métrica)
    const aggregate = useCallback((dimension, metric) => {
        const map = {};
        filteredCards.forEach(card => {
            const e = etapaDeCard(card);
            let key = '—', cor;
            if (dimension === 'funil') {
                const pipe = (pipelines || []).find(p => String(p.id) === String(card.pipelineId));
                key = pipe?.nome || 'Sem funil'; cor = pipe?.cor;
            }
            else if (dimension === 'etapa') { key = e.label; cor = e.cor; }
            else if (dimension === 'resultado') {
                if (card.resultado) {
                    key = card.resultado;
                    const matchingOption = (resultadosNegocio || []).find(r => String(r.id) === String(card.result_id));
                    cor = matchingOption?.cor || '#718096';
                } else {
                    const meta = TIPO_META[e.tipo] || TIPO_META.aberto;
                    key = meta.label;
                    cor = meta.cor;
                }
            }
            else if (dimension === 'canal') key = card.canal || 'Sem canal';
            else if (dimension === 'responsavel') key = card.responsavel || 'Sem responsável';
            else if (dimension === 'mes') key = (card.dataFechamento || '').slice(0, 7) || 'Sem data';
            if (!map[key]) map[key] = { name: key, value: 0, cor };
            const v = Number(card.valor) || 0;
            if (metric === 'count') map[key].value += 1;
            else if (metric === 'valorTotal') map[key].value += v;
            else if (metric === 'valorGanho') map[key].value += (resultadoDeCard(card) === 'ganho' ? v : 0);
        });
        let arr = Object.values(map).map((d, i) => ({ ...d, cor: d.cor || PALETTE[i % PALETTE.length] }));
        if (dimension === 'mes') arr.sort((a, b) => a.name.localeCompare(b.name));
        else arr.sort((a, b) => b.value - a.value);
        return arr;
    }, [filteredCards, etapaDeCard, pipelines, resultadosNegocio, resultadoDeCard]);

    // KPIs
    const stats = useMemo(() => {
        let ganho = 0, perdido = 0, aberto = 0, vGanho = 0, vPerdido = 0, vTotal = 0;
        filteredCards.forEach(card => {
            const tipo = resultadoDeCard(card);
            const v = Number(card.valor) || 0;
            vTotal += v;
            if (tipo === 'ganho') { ganho++; vGanho += v; }
            else if (tipo === 'perdido') { perdido++; vPerdido += v; }
            else aberto++;
        });
        const fechados = ganho + perdido;
        return {
            ganho, perdido, aberto, vGanho, vPerdido, vTotal,
            conversao: fechados > 0 ? (ganho / fechados) * 100 : 0,
            ticket: ganho > 0 ? vGanho / ganho : 0,
            total: filteredCards.length,
        };
    }, [filteredCards, resultadoDeCard]);

    const evolucaoMensal = useMemo(() => aggregate('mes', 'valorGanho'), [aggregate]);

    const atividadesPorTipo = useMemo(() => {
        const map = {};
        atividadesConcluidas.forEach(a => {
            const t = a.tipo || 'Outro';
            map[t] = (map[t] || 0) + 1;
        });
        return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
    }, [atividadesConcluidas]);

    const chartTooltipStyle = {
        backgroundColor: colorMode === 'dark' ? '#151719' : '#FFFFFF',
        borderColor: colorMode === 'dark' ? '#262930' : '#E2E8F0',
        borderRadius: '8px',
        color: colorMode === 'dark' ? '#FFFFFF' : '#000000',
    };

    const addWidget = () => {
        const dim = DIMENSOES.find(d => d.v === novo.dimensao);
        const met = METRICAS.find(m => m.v === novo.metrica);
        const vz = VIZ.find(x => x.v === novo.viz);
        setWidgets(w => [...w, {
            id: Date.now(),
            titulo: `${met.l} por ${dim.l}`,
            dimensao: novo.dimensao,
            metrica: novo.metrica,
            viz: novo.viz,
            vizLabel: vz.l,
        }]);
    };
    const removeWidget = (id) => setWidgets(w => w.filter(x => x.id !== id));

    const limparFiltros = () => {
        setFiltroFunil('todos'); setFiltroResponsavel('todos'); setFiltroCanal('todos'); setFiltroResultado('todos');
        setFiltroDataInicio(''); setFiltroDataFim('');
    };

    const periodoLabel = (filtroDataInicio || filtroDataFim)
        ? `${filtroDataInicio || '…'} a ${filtroDataFim || '…'}`
        : 'Todo o período';

    return (
        <VStack align="stretch" spacing={6}>
            {/* Header + ações */}
            <Flex className="no-print" justify="space-between" align="center" wrap="wrap" gap={4}
                p={6} borderRadius="2xl" border="1px solid" borderColor="borderPrimary"
                bg={colorMode === 'dark' ? 'linear-gradient(135deg, rgba(21,23,25,0.9) 0%, rgba(26,29,32,0.9) 100%)' : 'surface'} position="relative" overflow="hidden">
                <Box position="absolute" top="-100px" right="-100px" w="300px" h="300px" bg="radial-gradient(circle, rgba(1,233,117,0.12) 0%, transparent 70%)" pointerEvents="none" />
                <HStack spacing={4}>
                    <Flex w="50px" h="50px" borderRadius="xl" bg="rgba(1,233,117,0.15)" border="1px solid" borderColor="rgba(1,233,117,0.3)" align="center" justify="center">
                        <Icon as={AiOutlineBarChart} color="secondary" boxSize="26px" />
                    </Flex>
                    <VStack align="start" spacing={0}>
                        <Heading size="lg" color="textPrimary" fontWeight="900">Relatórios</Heading>
                        <Text color="textSecondary" fontSize="sm">Análises de negócios e atividades — monte as suas e exporte.</Text>
                    </VStack>
                </HStack>
                {aba === 'relatorios' && (
                    <Button leftIcon={<AiOutlineFilePdf />} bg="secondary" color="primary" _hover={{ bg: 'secondaryHover' }} fontWeight="800" onClick={() => window.print()}>
                        Gerar Relatório (PDF)
                    </Button>
                )}
            </Flex>

            {/* Abas */}
            <HStack className="no-print" bg="surface" border="1px solid" borderColor="borderPrimary" borderRadius="xl" p={1} spacing={1} alignSelf="flex-start" flexWrap="wrap">
                {ABAS.map(a => {
                    const active = aba === a.key;
                    const count = a.key === 'negocios' ? cardsConcluidos.length : a.key === 'atividades' ? atividadesConcluidas.length : null;
                    return (
                        <Flex key={a.key} as="button" align="center" gap={2} px={4} py={2} borderRadius="lg"
                            border="1px solid" borderColor={active ? 'rgba(1,233,117,0.45)' : 'borderSubtle'} bg={active ? 'secondarySubtle' : 'transparent'} color={active ? 'secondary' : 'textSecondary'}
                            fontWeight={active ? '800' : '600'} fontSize="sm"
                            _hover={{ bg: active ? 'secondarySubtle' : 'surfaceHover', color: active ? 'secondary' : 'textPrimary' }}
                            onClick={() => setAba(a.key)}>
                            <Icon as={a.icon} boxSize="16px" />
                            {a.label}{count != null ? ` (${count})` : ''}
                        </Flex>
                    );
                })}
            </HStack>

            {/* Filtros globais */}
            <Box className="no-print" p={5} bg="surface" border="1px solid" borderColor="borderPrimary" borderRadius="xl">
                <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(7, 1fr)" }} gap={4} alignItems="end">
                    <GridItem>
                        <FieldLabel>Funil</FieldLabel>
                        <SearchableSelect size="sm" value={filtroFunil} onChange={v => setFiltroFunil(v == null ? 'todos' : v)}
                            options={[{ value: 'todos', label: 'Todos os funis' }, ...(pipelines || []).map(p => ({ value: p.id, label: p.nome }))]}
                            placeholder="Buscar funil..." {...selectStyle} />
                    </GridItem>
                    <GridItem>
                        <FieldLabel>Responsável</FieldLabel>
                        <SearchableSelect size="sm" value={filtroResponsavel} onChange={v => setFiltroResponsavel(v == null ? 'todos' : v)}
                            options={[{ value: 'todos', label: 'Todos' }, ...employees.map(emp => ({ value: emp.id, label: emp.name }))]}
                            placeholder="Buscar responsável..." {...selectStyle} />
                    </GridItem>
                    <GridItem>
                        <FieldLabel>Canal</FieldLabel>
                        <SearchableSelect size="sm" value={filtroCanal} onChange={v => setFiltroCanal(v == null ? 'todos' : v)}
                            options={[{ value: 'todos', label: 'Todos' }, ...canais.map(c => ({ value: c.nome, label: c.nome }))]}
                            placeholder="Buscar canal..." {...selectStyle} />
                    </GridItem>
                    <GridItem>
                        <FieldLabel>Resultado</FieldLabel>
                        <SearchableSelect size="sm" value={filtroResultado} onChange={v => setFiltroResultado(v == null ? 'todos' : v)}
                            options={[
                                { value: 'todos', label: 'Todos' },
                                { value: 'ganho', label: 'Ganho' },
                                { value: 'perdido', label: 'Perdido' },
                                { value: 'aberto', label: 'Em aberto' },
                            ]}
                            placeholder="Buscar resultado..." {...selectStyle} />
                    </GridItem>
                    <GridItem>
                        <FieldLabel>Data Início</FieldLabel>
                        <Input type="date" size="sm" value={filtroDataInicio} onChange={e => setFiltroDataInicio(e.target.value)} {...dateStyle(colorMode)} />
                    </GridItem>
                    <GridItem>
                        <FieldLabel>Data Fim</FieldLabel>
                        <Input type="date" size="sm" value={filtroDataFim} onChange={e => setFiltroDataFim(e.target.value)} {...dateStyle(colorMode)} />
                    </GridItem>
                    <GridItem>
                        <Button size="sm" width="100%" variant="outline" borderColor="borderPrimary" color="textSecondary" leftIcon={<Icon as={AiOutlineCloseCircle} />}
                            _hover={{ bg: 'surfaceHover', color: 'textPrimary', borderColor: 'textSecondary' }} onClick={limparFiltros}>
                            Limpar
                        </Button>
                    </GridItem>
                </Grid>
            </Box>

            {/* === ABA RELATÓRIOS (área imprimível) === */}
            {aba === 'relatorios' && (
            <Box id="relatorio-print">
                {/* Cabeçalho só na impressão */}
                <Box className="only-print" mb={4}>
                    <Heading size="md" color="black">Relatório Comercial — Nogle</Heading>
                    <Text fontSize="sm" color="gray.600">Período: {periodoLabel} · Gerado em {new Date().toLocaleString('pt-BR')}</Text>
                </Box>

                {/* KPIs */}
                <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={4} mb={6}>
                    <KpiCard icon={AiOutlinePartition} color="#9F7AEA" label="Negócios" value={fmtInt(stats.total)} sub={`${stats.ganho} ganhos · ${stats.perdido} perdidos`} />
                    <KpiCard icon={AiOutlineRise} color="#01E975" label="Conversão" value={`${stats.conversao.toFixed(1)}%`} sub="ganhos ÷ fechados" />
                    <KpiCard icon={AiOutlineDollarCircle} color="#01E975" label="Valor ganho" value={fmtMoney(stats.vGanho)} />
                    <KpiCard icon={AiOutlineCloseCircle} color="#FC8181" label="Valor perdido" value={fmtMoney(stats.vPerdido)} />
                    <KpiCard icon={AiOutlineTrophy} color="#ECC94B" label="Ticket médio" value={fmtMoney(stats.ticket)} sub="por negócio ganho" />
                    <KpiCard icon={AiOutlineSchedule} color="#4299E1" label="Atividades" value={fmtInt(atividadesConcluidas.length)} sub="realizadas" />
                </SimpleGrid>

                {/* Análises fixas */}
                <Grid templateColumns={{ base: "1fr", lg: "repeat(2, 1fr)" }} gap={6} mb={6}>
                    <ChartCard title="Resultado dos Negócios">
                        <PieViz data={aggregate('resultado', 'count')} tooltipStyle={chartTooltipStyle} money={false} />
                    </ChartCard>
                    <ChartCard title="Negócios por Etapa">
                        <BarViz data={aggregate('etapa', 'count')} tooltipStyle={chartTooltipStyle} colorMode={colorMode} money={false} />
                    </ChartCard>
                    <ChartCard title="Evolução do Valor Ganho (por mês)">
                        {evolucaoMensal.length === 0
                            ? <EmptyChart />
                            : (
                                <Box h="250px" w="100%">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={evolucaoMensal} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke={colorMode === 'dark' ? '#262920' : '#E2E8F0'} vertical={false} />
                                            <XAxis dataKey="name" stroke={colorMode === 'dark' ? '#718096' : '#4A5568'} fontSize={10} tickLine={false} />
                                            <YAxis stroke={colorMode === 'dark' ? '#718096' : '#4A5568'} fontSize={10} tickLine={false} />
                                            <ChartTooltip contentStyle={chartTooltipStyle} itemStyle={{ color: chartTooltipStyle.color }} labelStyle={{ color: chartTooltipStyle.color }} formatter={(v) => fmtMoney(v)} />
                                            <Line type="monotone" dataKey="value" stroke="#01E975" strokeWidth={3} dot={{ r: 4, fill: '#01E975' }} name="Valor ganho" />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </Box>
                            )}
                    </ChartCard>
                    <ChartCard title="Atividades por Tipo">
                        {atividadesPorTipo.length === 0
                            ? <EmptyChart />
                            : (
                                <Box h="250px" w="100%">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={atividadesPorTipo} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke={colorMode === 'dark' ? '#262920' : '#E2E8F0'} vertical={false} />
                                            <XAxis dataKey="name" stroke={colorMode === 'dark' ? '#718096' : '#4A5568'} fontSize={10} tickLine={false} />
                                            <YAxis stroke={colorMode === 'dark' ? '#718096' : '#4A5568'} fontSize={10} tickLine={false} allowDecimals={false} />
                                            <ChartTooltip contentStyle={chartTooltipStyle} itemStyle={{ color: chartTooltipStyle.color }} labelStyle={{ color: chartTooltipStyle.color }} cursor={{ fill: 'transparent' }} />
                                            <Bar dataKey="value" fill="#4299E1" radius={[4, 4, 0, 0]} barSize={32} name="Atividades" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </Box>
                            )}
                    </ChartCard>
                </Grid>

                {/* Construtor de análises personalizadas */}
                <Box className="no-print" p={5} bg="surface" border="1px solid" borderColor="borderPrimary" borderRadius="xl" mb={6}>
                    <HStack mb={4} spacing={2}>
                        <Icon as={AiOutlinePlus} color="secondary" />
                        <Text fontWeight="800" color="textPrimary">Criar análise personalizada</Text>
                    </HStack>
                    <Grid templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }} gap={4} alignItems="end">
                        <GridItem>
                            <FieldLabel>Agrupar por (dimensão)</FieldLabel>
                            <SearchableSelect size="sm" value={novo.dimensao} onChange={v => v != null && setNovo(p => ({ ...p, dimensao: v }))}
                                options={DIMENSOES.map(d => ({ value: d.v, label: d.l }))}
                                placeholder="Buscar dimensão..." {...selectStyle} />
                        </GridItem>
                        <GridItem>
                            <FieldLabel>Métrica</FieldLabel>
                            <SearchableSelect size="sm" value={novo.metrica} onChange={v => v != null && setNovo(p => ({ ...p, metrica: v }))}
                                options={METRICAS.map(m => ({ value: m.v, label: m.l }))}
                                placeholder="Buscar métrica..." {...selectStyle} />
                        </GridItem>
                        <GridItem>
                            <FieldLabel>Visualização</FieldLabel>
                            <SearchableSelect size="sm" value={novo.viz} onChange={v => v != null && setNovo(p => ({ ...p, viz: v }))}
                                options={VIZ.map(x => ({ value: x.v, label: x.l }))}
                                placeholder="Buscar visualização..." {...selectStyle} />
                        </GridItem>
                        <GridItem>
                            <Button size="sm" width="100%" bg="secondary" color="primary" _hover={{ bg: 'secondaryHover' }} fontWeight="800" leftIcon={<AiOutlinePlus />} onClick={addWidget}>
                                Adicionar análise
                            </Button>
                        </GridItem>
                    </Grid>
                </Box>

                {/* Widgets personalizados */}
                {widgets.length > 0 && (
                    <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                        {widgets.map(w => {
                            const data = aggregate(w.dimensao, w.metrica);
                            const money = METRICAS.find(m => m.v === w.metrica)?.money;
                            return (
                                <ChartCard key={w.id} title={w.titulo} onRemove={() => removeWidget(w.id)}>
                                    {data.length === 0 ? <EmptyChart /> : (
                                        w.viz === 'pie' ? <PieViz data={data} tooltipStyle={chartTooltipStyle} money={money} />
                                        : w.viz === 'table' ? <TableViz data={data} money={money} />
                                        : <BarViz data={data} tooltipStyle={chartTooltipStyle} colorMode={colorMode} money={money} />
                                    )}
                                </ChartCard>
                            );
                        })}
                    </SimpleGrid>
                )}
            </Box>
            )}

            {/* === ABA NEGÓCIOS (tabela) === */}
            {aba === 'negocios' && (
                filteredCards.length === 0 ? (
                    <EmptyState label="Nenhum negócio concluído no filtro atual." />
                ) : (
                    <Box bg="surface" border="1px solid" borderColor="borderPrimary" borderRadius="xl" overflow="hidden">
                        <TableContainer>
                            <Table variant="simple" size="sm">
                                <Thead>
                                    <Tr>
                                        <ThCell>Negócio</ThCell>
                                        <ThCell>Empresa</ThCell>
                                        <ThCell>Etapa</ThCell>
                                        <ThCell>Resultado</ThCell>
                                        <ThCell>Responsável</ThCell>
                                        <ThCell isNumeric>Valor</ThCell>
                                        <ThCell>Fechado em</ThCell>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {grupos.flatMap(g => g.cards)
                                        .slice((pageNeg - 1) * ITENS_POR_PAGINA, pageNeg * ITENS_POR_PAGINA)
                                        .map(card => {
                                        const e = etapaDeCard(card);
                                        let outcomeLabel = '';
                                        let outcomeColor = '';
                                        
                                        if (card.canceled) {
                                            outcomeLabel = 'Cancelado';
                                            outcomeColor = '#FC8181';
                                        } else if (card.resultado) {
                                            outcomeLabel = card.resultado;
                                            const matchingOption = (resultadosNegocio || []).find(r => String(r.id) === String(card.result_id));
                                            outcomeColor = matchingOption?.cor || '#718096';
                                        } else {
                                            const meta = TIPO_META[e.tipo] || TIPO_META.aberto;
                                            outcomeLabel = meta.label;
                                            outcomeColor = meta.cor;
                                        }
                                        const empresa = resolverEmpresa(card);
                                        const responsavel = resolverResponsavel(card);
                                        return (
                                            <Tr key={card.id} cursor="pointer" _hover={{ bg: colorMode === 'dark' ? 'whiteAlpha.50' : 'blackAlpha.50' }}
                                                onClick={() => abrirDetalhe('negocio', { ...card, empresa, responsavel }, e)}>
                                                <Td borderColor="borderPrimary" fontWeight="700" color="textPrimary" maxW="220px"><Text noOfLines={1}>{card.titulo || 'Negócio'}</Text></Td>
                                                <Td borderColor="borderPrimary" color="textSecondary"><Text noOfLines={1}>{empresa || '—'}</Text></Td>
                                                <Td borderColor="borderPrimary">
                                                    <HStack spacing={2}>
                                                        <Box w="8px" h="8px" borderRadius="full" bg={e.cor} flexShrink={0} />
                                                        <Text color="textPrimary" noOfLines={1}>{e.label}</Text>
                                                    </HStack>
                                                </Td>
                                                <Td borderColor="borderPrimary">
                                                    <Badge bg={`${outcomeColor}22`} color={outcomeColor} borderRadius="full" px={2} fontWeight="800" textTransform="none">{outcomeLabel}</Badge>
                                                </Td>
                                                <Td borderColor="borderPrimary" color="textSecondary"><Text noOfLines={1}>{responsavel || '—'}</Text></Td>
                                                <Td borderColor="borderPrimary" isNumeric fontWeight="800" color="textPrimary">{fmtMoney(card.valor)}</Td>
                                                <Td borderColor="borderPrimary" color="textSecondary">{fmtBR(card.dataFechamento)}</Td>
                                            </Tr>
                                        );
                                    })}
                                </Tbody>
                            </Table>
                        </TableContainer>
                        <Paginacao page={pageNeg} setPage={setPageNeg} total={filteredCards.length} perPage={ITENS_POR_PAGINA} colorMode={colorMode} />
                    </Box>
                )
            )}

            {/* === ABA ATIVIDADES (tabela) === */}
            {aba === 'atividades' && (
                filteredAtividades.length === 0 ? (
                    <EmptyState label="Nenhuma atividade concluída no filtro atual." />
                ) : (
                    <Box bg="surface" border="1px solid" borderColor="borderPrimary" borderRadius="xl" overflow="hidden">
                        <TableContainer>
                            <Table variant="simple" size="sm">
                                <Thead>
                                    <Tr>
                                        <ThCell>Atividade</ThCell>
                                        <ThCell>Tipo</ThCell>
                                        <ThCell>Responsável</ThCell>
                                        <ThCell>Empresa</ThCell>
                                        <ThCell>Data</ThCell>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {filteredAtividades
                                        .slice((pageAtv - 1) * ITENS_POR_PAGINA, pageAtv * ITENS_POR_PAGINA)
                                        .map(atv => (
                                        <Tr key={atv.id} cursor="pointer" _hover={{ bg: colorMode === 'dark' ? 'whiteAlpha.50' : 'blackAlpha.50' }} onClick={() => abrirDetalhe('atividade', atv)}>
                                            <Td borderColor="borderPrimary" fontWeight="700" color="textPrimary" maxW="240px"><Text noOfLines={1}>{atv.nome || 'Atividade'}</Text></Td>
                                            <Td borderColor="borderPrimary">
                                                {atv.tipo ? <Badge bg="rgba(66,153,225,0.18)" color="#4299E1" borderRadius="full" px={2} fontWeight="800" textTransform="none">{atv.tipo}</Badge> : '—'}
                                            </Td>
                                            <Td borderColor="borderPrimary" color="textSecondary"><Text noOfLines={1}>{atv.responsavel || '—'}</Text></Td>
                                            <Td borderColor="borderPrimary" color="textSecondary"><Text noOfLines={1}>{atv.empresa || '—'}</Text></Td>
                                            <Td borderColor="borderPrimary" color="textSecondary">{fmtBR(atv.dataInicio)}{atv.horaInicio && atv.horaInicio !== '00:00' ? ` · ${atv.horaInicio}` : ''}</Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        </TableContainer>
                        <Paginacao page={pageAtv} setPage={setPageAtv} total={filteredAtividades.length} perPage={ITENS_POR_PAGINA} colorMode={colorMode} />
                    </Box>
                )
            )}

            <DetalheModal isOpen={isDetOpen} onClose={onDetClose} detalhe={detalhe} colorMode={colorMode} onReabrir={() => setConfirmReabrirAberto(true)} reabrindo={reabrindo} />

            {/* Confirmação padrão de ação (retornar ao Pipeline/Atividades) — evita cliques acidentais */}
            <ConfirmActionDialog
                isOpen={confirmReabrirAberto}
                onClose={() => setConfirmReabrirAberto(false)}
                onConfirm={async () => { setConfirmReabrirAberto(false); await handleReabrir(); }}
                title={detalhe?.kind === 'negocio' ? 'Retornar Negócio ao Pipeline' : 'Retornar Atividade'}
                itemName={detalhe?.data?.titulo || detalhe?.data?.nome}
                description={detalhe?.kind === 'negocio'
                    ? 'O negócio sai dos Relatórios e volta para a primeira etapa aberta do funil.'
                    : 'A atividade sai dos Relatórios e volta para a lista de Atividades.'}
                confirmLabel="Sim, retornar"
                scheme="green"
                icon={AiOutlineRollback}
            />
        </VStack>
    );
};

// ── Sub-componentes de visualização ──
const FieldLabel = ({ children }) => (
    <Text fontSize="xs" fontWeight="800" color="textSecondary" mb={1.5} textTransform="uppercase" letterSpacing="0.5px">{children}</Text>
);
const selectStyle = { bg: 'surfaceHover', borderColor: 'borderPrimary', borderRadius: 'lg', _focus: { borderColor: 'secondary', boxShadow: 'none' } };
const dateStyle = (colorMode) => ({
    bg: 'surfaceHover', borderColor: 'borderPrimary', borderRadius: 'lg', color: 'textPrimary',
    css: { "&::-webkit-calendar-picker-indicator": { filter: colorMode === 'dark' ? 'invert(1)' : 'none' } },
    _focus: { borderColor: 'secondary', boxShadow: 'none' },
});

const ChartCard = ({ title, children, onRemove }) => (
    <Box bg="surface" border="1px solid" borderColor="borderPrimary" borderRadius="xl" p={5}>
        <HStack justify="space-between" mb={4}>
            <Text fontWeight="800" color="textPrimary" fontSize="md">{title}</Text>
            {onRemove && (
                <IconButton className="no-print" aria-label="Remover" icon={<AiOutlineDelete />} size="xs" variant="ghost" color="textSecondary" _hover={{ color: 'red.400', bg: 'rgba(229,62,62,0.1)' }} onClick={onRemove} />
            )}
        </HStack>
        {children}
    </Box>
);

const EmptyChart = () => (
    <Flex h="250px" align="center" justify="center"><Text color="textSecondary" fontSize="sm">Sem dados no filtro atual.</Text></Flex>
);

const EmptyState = ({ label }) => (
    <Box bg="surface" border="1px dashed" borderColor="borderPrimary" borderRadius="xl" p={10} textAlign="center">
        <Icon as={AiOutlinePartition} color="textMuted" boxSize="32px" mb={2} />
        <Text color="textMuted" fontSize="sm">{label}</Text>
    </Box>
);

const fmtVal = (v, money) => money ? fmtMoney(v) : fmtInt(v);

const BarViz = ({ data, tooltipStyle, colorMode, money }) => (
    <Box h="250px" w="100%">
        <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 10, right: 10, left: money ? 10 : -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={colorMode === 'dark' ? '#262920' : '#E2E8F0'} vertical={false} />
                <XAxis dataKey="name" stroke={colorMode === 'dark' ? '#718096' : '#4A5568'} fontSize={10} tickLine={false} />
                <YAxis stroke={colorMode === 'dark' ? '#718096' : '#4A5568'} fontSize={10} tickLine={false} allowDecimals={false} tickFormatter={money ? (v) => `R$${(v / 1000).toFixed(0)}k` : undefined} />
                <ChartTooltip contentStyle={tooltipStyle} itemStyle={{ color: tooltipStyle.color }} labelStyle={{ color: tooltipStyle.color }} cursor={{ fill: 'transparent' }} formatter={(v) => fmtVal(v, money)} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]} barSize={28} name="Total">
                    {data.map((entry, i) => <Cell key={i} fill={entry.cor} />)}
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    </Box>
);

const PieViz = ({ data, tooltipStyle, money }) => {
    const total = data.reduce((s, d) => s + d.value, 0);
    return (
        <HStack spacing={6} align="center" justify="center" h="250px" w="100%">
            <Box w="55%" h="100%">
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie data={data} cx="50%" cy="50%" innerRadius={55} outerRadius={80} paddingAngle={4} dataKey="value">
                            {data.map((entry, i) => <Cell key={i} fill={entry.cor} />)}
                        </Pie>
                        <ChartTooltip contentStyle={tooltipStyle} itemStyle={{ color: tooltipStyle.color }} labelStyle={{ color: tooltipStyle.color }} formatter={(v) => fmtVal(v, money)} />
                    </PieChart>
                </ResponsiveContainer>
            </Box>
            <VStack align="start" spacing={2} w="45%" maxH="100%" overflowY="auto">
                {data.map((d, i) => {
                    const pct = total > 0 ? (d.value / total) * 100 : 0;
                    return (
                        <HStack key={i} spacing={2} w="100%">
                            <Box w="10px" h="10px" borderRadius="full" bg={d.cor} flexShrink={0} />
                            <Text fontSize="xs" color="textPrimary" fontWeight="700" noOfLines={1} flex={1}>{d.name}</Text>
                            <Text fontSize="xs" color="textSecondary">{fmtVal(d.value, money)} ({pct.toFixed(0)}%)</Text>
                        </HStack>
                    );
                })}
            </VStack>
        </HStack>
    );
};

const TableViz = ({ data, money }) => {
    const total = data.reduce((s, d) => s + d.value, 0);
    return (
        <VStack align="stretch" spacing={0} border="1px solid" borderColor="borderPrimary" borderRadius="lg" overflow="hidden">
            {data.map((d, i) => (
                <HStack key={i} justify="space-between" px={3} py={2} bg={i % 2 ? 'transparent' : 'surfaceHover'}>
                    <HStack spacing={2} minW={0}>
                        <Box w="10px" h="10px" borderRadius="full" bg={d.cor} flexShrink={0} />
                        <Text fontSize="sm" color="textPrimary" fontWeight="600" noOfLines={1}>{d.name}</Text>
                    </HStack>
                    <Text fontSize="sm" color="textPrimary" fontWeight="800" flexShrink={0}>{fmtVal(d.value, money)}</Text>
                </HStack>
            ))}
            <HStack justify="space-between" px={3} py={2} borderTop="1px solid" borderColor="borderPrimary" bg="surfaceHover">
                <Text fontSize="sm" color="textSecondary" fontWeight="800" textTransform="uppercase">Total</Text>
                <Text fontSize="sm" color="secondary" fontWeight="900">{fmtVal(total, money)}</Text>
            </HStack>
        </VStack>
    );
};

// ── Tabela / Detalhes ──
const ThCell = ({ children, ...p }) => (
    <Th color="textSecondary" borderColor="borderPrimary" fontSize="10px" letterSpacing="1px" {...p}>{children}</Th>
);

const Paginacao = ({ page, setPage, total, perPage, colorMode }) => {
    const pages = Math.ceil(total / perPage);
    if (pages <= 1) return null;
    return (
        <HStack justify="center" spacing={2} py={3} borderTop="1px solid" borderColor="borderPrimary" bg={colorMode === 'dark' ? 'whiteAlpha.50' : 'blackAlpha.50'} flexWrap="wrap">
            <Button size="xs" variant="outline" borderColor="borderPrimary" color="textSecondary" isDisabled={page === 1}
                onClick={() => setPage(p => Math.max(1, p - 1))} _hover={{ bg: colorMode === 'dark' ? 'whiteAlpha.100' : 'blackAlpha.100', color: 'textPrimary' }}>Anterior</Button>
            {Array.from({ length: pages }, (_, i) => i + 1).map(n => (
                <Button key={n} size="xs" onClick={() => setPage(n)} fontWeight="800"
                    bg={page === n ? 'secondary' : 'transparent'} color={page === n ? 'primary' : 'textSecondary'}
                    border="1px solid" borderColor={page === n ? 'secondary' : 'borderPrimary'}
                    _hover={{ bg: page === n ? 'secondaryHover' : (colorMode === 'dark' ? 'whiteAlpha.100' : 'blackAlpha.100'), color: page === n ? 'primary' : 'textPrimary' }}>{n}</Button>
            ))}
            <Button size="xs" variant="outline" borderColor="borderPrimary" color="textSecondary" isDisabled={page === pages}
                onClick={() => setPage(p => Math.min(pages, p + 1))} _hover={{ bg: colorMode === 'dark' ? 'whiteAlpha.100' : 'blackAlpha.100', color: 'textPrimary' }}>Próxima</Button>
        </HStack>
    );
};

const DetField = ({ icon, label, value, accent = '#718096', children, span }) => {
    const { colorMode } = useColorMode();
    return (
        <Box bg={colorMode === 'dark' ? 'whiteAlpha.50' : 'blackAlpha.50'} border="1px solid" borderColor="borderPrimary" borderRadius="xl" p={3} gridColumn={span ? { md: 'span 2' } : undefined}>
            <HStack spacing={2} mb={1}>
                {icon && <Icon as={icon} color={accent} boxSize="12px" />}
                <Text fontSize="9px" color="textSecondary" fontWeight="900" textTransform="uppercase" letterSpacing="1px">{label}</Text>
            </HStack>
            {children || <Text fontSize="sm" color="textPrimary" fontWeight="700" lineHeight="1.4" whiteSpace="pre-wrap">{value || '—'}</Text>}
        </Box>
    );
};

const DetalheModal = ({ isOpen, onClose, detalhe, onReabrir, reabrindo }) => {
    const { colorMode } = useColorMode();
    const isNeg = detalhe?.kind === 'negocio';
    const d = detalhe?.data || {};
    const etapa = detalhe?.etapa;
    const tipoMeta = etapa ? (TIPO_META[etapa.tipo] || TIPO_META.aberto) : null;
    const heroCor = isNeg ? (etapa?.cor || '#718096') : '#4299E1';

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg" scrollBehavior="inside">
            <ModalOverlay backdropFilter="blur(8px)" bg="blackAlpha.700" />
            <ModalContent bg="surface" border="1px solid" borderColor="borderPrimary" color="textPrimary" borderRadius="2xl" overflow="hidden">
                <ModalCloseButton color="textSecondary" zIndex={2} />
                <ModalBody pt={0} pb={2} px={6}>
                    {detalhe && (
                        <VStack spacing={5} align="stretch">
                            <Box mx={-6} pt={10} pb={5} px={6} bgGradient={`linear(to-b, ${heroCor}26, transparent)`}>
                                <Flex direction="column" align="center" gap={3}>
                                    <Flex boxSize="60px" borderRadius="2xl" align="center" justify="center" bg={`${heroCor}1A`} border="1px solid" borderColor={`${heroCor}40`} boxShadow={`0 10px 28px ${heroCor}22`}>
                                        <Icon as={isNeg ? AiOutlinePartition : AiOutlineSchedule} color={heroCor} boxSize="28px" />
                                    </Flex>
                                    <VStack spacing={2}>
                                        <Text fontSize="xl" fontWeight="900" textAlign="center" lineHeight="1.2">{isNeg ? (d.titulo || 'Negócio') : (d.nome || 'Atividade')}</Text>
                                        <HStack spacing={2} flexWrap="wrap" justify="center">
                                            {isNeg && etapa && (
                                                <Badge bg={`${etapa.cor}22`} color={etapa.cor} fontSize="xs" fontWeight="800" borderRadius="full" px={2.5} py={0.5}>{etapa.label}</Badge>
                                            )}
                                            {isNeg && tipoMeta && (
                                                <Badge bg={`${tipoMeta.cor}22`} color={tipoMeta.cor} fontSize="xs" fontWeight="800" borderRadius="full" px={2.5} py={0.5}>{tipoMeta.label}</Badge>
                                            )}
                                            {!isNeg && d.tipo && (
                                                <Badge bg="rgba(66,153,225,0.18)" color="#4299E1" fontSize="xs" fontWeight="800" borderRadius="full" px={2.5} py={0.5}>{d.tipo}</Badge>
                                            )}
                                        </HStack>
                                    </VStack>
                                </Flex>
                            </Box>

                            {isNeg ? (
                                <SimpleGrid columns={2} spacing={3}>
                                    <DetField icon={AiOutlineDollarCircle} label="Valor" accent="#01E975" value={fmtMoney(d.valor)} />
                                    {tipoMeta && (
                                        <DetField icon={AiOutlineTrophy} label="Resultado" accent={tipoMeta.cor}>
                                            <Badge bg={`${tipoMeta.cor}22`} color={tipoMeta.cor} borderRadius="full" px={2} fontWeight="800" textTransform="none">{tipoMeta.label}</Badge>
                                        </DetField>
                                    )}
                                    {etapa && (
                                        <DetField icon={AiOutlinePartition} label="Etapa" accent={etapa.cor}>
                                            <HStack spacing={2}>
                                                <Box w="10px" h="10px" borderRadius="full" bg={etapa.cor} />
                                                <Text fontSize="sm" fontWeight="700" color="textPrimary">{etapa.label}</Text>
                                            </HStack>
                                        </DetField>
                                    )}
                                    <DetField icon={AiOutlineShop} label="Empresa / Marca" accent="#ED8936" value={d.empresa} />
                                    <DetField icon={AiOutlineUser} label="Responsável" accent="#9F7AEA" value={d.responsavel} />
                                    <DetField icon={AiOutlinePartition} label="Canal" accent="#4299E1" value={d.canal} />
                                    <DetField icon={AiOutlineCalendar} label="Início" accent="#4299E1" value={fmtBR(d.dataInicio)} />
                                    <DetField icon={AiOutlineCalendar} label="Fechamento" accent="#FC8181" value={fmtBR(d.dataFechamento)} />
                                    {d.meetLink && (
                                        <DetField icon={AiOutlineVideoCamera} label="Reunião" accent="#4299E1" span>
                                            <Link href={d.meetLink} isExternal color="#4299E1" fontSize="sm" fontWeight="700" noOfLines={1}>{d.meetLink}</Link>
                                        </DetField>
                                    )}
                                    {d.observacoes && <DetField label="Observações" value={d.observacoes} span />}
                                </SimpleGrid>
                            ) : (
                                <SimpleGrid columns={2} spacing={3}>
                                    <DetField icon={AiOutlineUser} label="Responsável" accent="#9F7AEA" value={d.responsavel} />
                                    <DetField icon={AiOutlineShop} label="Empresa / Marca" accent="#ED8936" value={d.empresa} />
                                    <DetField icon={AiOutlineCalendar} label="Início" accent="#4299E1" value={`${fmtBR(d.dataInicio)}${d.horaInicio && d.horaInicio !== '00:00' ? ` às ${d.horaInicio}` : ''}`} />
                                    <DetField icon={AiOutlineCalendar} label="Fim" accent="#FC8181" value={`${fmtBR(d.dataFim || d.dataInicio)}${d.horaFim && d.horaFim !== '00:00' ? ` às ${d.horaFim}` : ''}`} />
                                    {d.observacao && <DetField label="Observação" value={d.observacao} span />}
                                </SimpleGrid>
                            )}
                        </VStack>
                    )}
                </ModalBody>
                <ModalFooter borderTop="1px solid" borderColor="borderPrimary" mt={2} gap={3}>
                    <Button variant="ghost" color="textSecondary" _hover={{ bg: colorMode === 'dark' ? 'whiteAlpha.100' : 'blackAlpha.100' }} onClick={onClose} size="sm">Fechar</Button>
                    {onReabrir && (
                        <Button
                            leftIcon={<Icon as={AiOutlineRollback} />}
                            bg="secondary"
                            color="primary"
                            _hover={{ bg: 'secondaryHover' }}
                            fontWeight="800"
                            size="sm"
                            isLoading={reabrindo}
                            onClick={onReabrir}
                        >
                            {isNeg ? 'Retornar ao Pipeline' : 'Retornar às Atividades'}
                        </Button>
                    )}
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default Relatorios;
